import { useCallback, useMemo, useRef } from 'react';

import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import _isEqual from 'lodash/isEqual';

import { getPixallId } from '@atc/pixall-fns';

import getSelectedFilterOptions from '@/utilities/getSelectedFilterOptions';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import suggestedSearchesDuck from '@/ducks/suggestedSearchesDuck';

import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

export function useSuggestedSearches() {
    const dispatch = useDispatch();

    const suggestions = useSelector(suggestedSearchesDuck.selectors.getSuggestions, _isEqual) || [];
    const isLoading = useSelector(suggestedSearchesDuck.selectors.getIsLoading, _isEqual);

    const filtersOptions = useSelector(srpFiltersDuck.selectors.getFiltersOptions, _isEqual);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues, _isEqual);
    const personalizationData = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalizationRaw, _isEqual);
    const pixallId = useMemo(() => getPixallId(), []);
    const [cookies = {}] = useCookies(['auth_consumer_id']);
    const consumerId = cookies.auth_consumer_id;

    const selectedFilterOptions = useMemo(
        () => {
            const { filterSelections = [], ...rest } = getSelectedFilterOptions(filtersOptions, filtersValues) || {};
            return [...filterSelections, ...Object.values(rest)];
        },
        [filtersOptions, filtersValues]
    );

    const params = useMemo(() => ({
        selectedFilterOptions,
        personalizationData,
        pixallId,
        consumerId,
    }), [selectedFilterOptions, personalizationData, pixallId, consumerId]);

    const lastLoadedParamsRef = useRef(null);

    const loadSuggestions = useCallback(() => {
        if (!_isEqual(params, lastLoadedParamsRef.current)) {
            dispatch(suggestedSearchesDuck.creators.loadSuggestions(params));
            lastLoadedParamsRef.current = params;
        }
    }, [dispatch, params]);

    return {
        suggestions,
        isLoading,
        loadSuggestions,
    };
}
